import React, {useEffect} from 'react'
import {observer} from 'mobx-react-lite'
import {BaseButton, Box, Button, Flex, Stack, Text} from '../../../vanilla'
import {useIntl} from 'react-intl'
import {
  BasketIcon,
  DeliveryMapWithPinIcon,
  EditIcon,
  ExclamationCircleIcon,
} from '../../../components/icons'
import {useBasketStore, useGlobalStore, useOrderStore} from '../../../store/hooks/useStore'
import {Link} from '../../../components/link'
import {useRect} from '@reach/rect'
import {editModeActionBoxContainer, editModeBasketActionText} from './styles.css'
import {DateCalendar} from '../../../components/date-calendar'
import {DeliveryDateTime} from '../../../components/book-delivery/delivery-date-time'
import useDeliveryBooking from '../../../hooks/use-delivery-booking'
import {useDeliveryReservationTime} from '../../../hooks/use-delivery-reservation-time'
import { useSlotExpiration } from '../../../store/hooks/useSlotExpiration'
import useNavigation from '../../../hooks/use-navigation'
import { DeliveryTimeAndDate } from '../../../components/book-delivery/delivery-time-and-date'

interface OrderEditModeActionContainerProps {
  shoppingBasketRect: ReturnType<typeof useRect>
}

const OrderEditModeActionContainer = ({shoppingBasketRect}: OrderEditModeActionContainerProps) => {
  const {formatNumber} = useIntl()

  const {currency} = useGlobalStore()
  const {orderEditProductItemsDelta, orderEditDeliverySlotChanged, orderTotal} = useOrderStore()
  const {count, currentDeliverySlot, addressDisplay} =
    useBasketStore()
  const {isReservationExpired} = useSlotExpiration()
  const {reserveSlot, loading, getDeliverySlots} = useDeliveryBooking()
  const navigate = useNavigation()

  const productItemsUpdated = !!Object.keys(orderEditProductItemsDelta).length

  // If lost the current delivery slot, get the delivery slots again
  useEffect(() => { 
    if (!currentDeliverySlot.timeSlot) {
      getDeliverySlots()
    }
  }, [currentDeliverySlot.timeSlot])

  const deliveryDate = currentDeliverySlot.timeSlot?.windowEndTime
    ? new Date(currentDeliverySlot.timeSlot.windowEndTime)
    : null

  return (
    <Stack spacing="0px" align="flex-start" width="full" alignSelf="stretch">
      <Flex
        borderBottom="1px"
        borderColor="gray200"
        padding="16px"
        width="full"
        paddingLeft="28px"
        justify="space-between"
        align="center"
        display={['none', 'flex']}
      >
        <Text variant="heading1">Your Order</Text>
        {(productItemsUpdated || orderEditDeliverySlotChanged) && (
          <Flex align="center" bg="promotionColorLight" padding="4px" gap="4px">
            <ExclamationCircleIcon boxSize="16px" color="promotionColorDark" marginBottom="2px" />
            <Text variant="text4" lineHeight="shorter" data-test-selector="order-updated-checkout">
              Order updated - Checkout to apply
            </Text>
          </Flex>
        )}
      </Flex>
      {(productItemsUpdated || orderEditDeliverySlotChanged) && (
        <Flex
          bg="promotionColorLight"
          padding="4px"
          gap="4px"
          width="full"
          justify={['center', 'flex-start']}
          align="center"
          display={['flex', 'none']}
        >
          <ExclamationCircleIcon boxSize="16px" color="promotionColorDark" marginBottom="2px" />
          <Text variant="text4" lineHeight="shorter" data-test-selector="order-updated-checkout">
            Order updated - Checkout to apply
          </Text>
        </Flex>
      )}
      <Box paddingX="28px" paddingY="24px" width="full">
        <Flex
          direction="row"
          flexWrap={['wrap', 'wrap', 'nowrap']}
          className={editModeActionBoxContainer}
        >
          {!isReservationExpired() ? (
            <>
              <Flex gap="20px" flexShrink="0">
                <BasketIcon boxSize="56px" color="gray800" />
                <Stack spacing="0px" align="flex-start">
                  <Box>
                    <Text color="accent1" variant="text4" lineHeight="short">
                      Your Basket
                    </Text>
                    <Flex gap="4px" align="center">
                      {productItemsUpdated && (
                        <ExclamationCircleIcon
                          boxSize="16px"
                          color="promotionColorDark"
                          marginBottom="2px"
                        />
                      )}
                      {currency && (
                        <Text variant="text4" data-test-selector="items-count">
                          {count} Item(s) -{' '}
                          {formatNumber(orderTotal || 0, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                            currency,
                            style: 'currency',
                          })}
                        </Text>
                      )}
                    </Flex>
                  </Box>
                  <BaseButton
                    variant="unstyled"
                    display="inline-flex"
                    paddingX="0px"
                    height="auto"
                    gap="2px"
                    onClick={() => {
                      window.scrollTo({
                        top: shoppingBasketRect?.top,
                        behavior: 'smooth',
                      })
                    }}
                  >
                    <Text variant="text4" data-test-selector="add-remove-items" color="accent5" className={editModeBasketActionText}>
                      Add/Remove items
                    </Text>
                  </BaseButton>
                </Stack>
              </Flex>
              <Flex gap="20px" flexShrink="1">
                {deliveryDate && <DateCalendar date={deliveryDate} />}
                <Stack spacing="0px" align="flex-start">
                  <Box>
                    <Text color="accent1" variant="text4" lineHeight="short">
                      Your delivery time:
                    </Text>
                    <Flex gap="4px" align="center">
                      {orderEditDeliverySlotChanged && (
                        <ExclamationCircleIcon
                          boxSize="16px"
                          color="promotionColorDark"
                          marginBottom="2px"
                        />
                      )}
                      {currentDeliverySlot.timeSlot && deliveryDate && (
                        <DeliveryTimeAndDate
                          startHour={currentDeliverySlot.timeSlot.startHour}
                          endHour={currentDeliverySlot.timeSlot.endHour}
                          showDate={true}
                          dateFormat='EEEE do MMMM'
                          textProps={{variant: 'text4'}}
                        />
                      )}
                    </Flex>
                  </Box>
                  {!isReservationExpired() && (
                    <BaseButton
                      variant="unstyled"
                      display="inline-flex"
                      paddingX="0px"
                      height="auto"
                      gap="2px"
                      onClick={() => navigate('/book-delivery', 'push', {redirectTo: '/basket'})}
                    >
                      <Text variant="text4" data-test-selector="change-delivery-slot" color="accent5" className={editModeBasketActionText}>
                        Change delivery slot
                      </Text>
                    </BaseButton>
                  )}
                </Stack>
              </Flex>
            </>
          ) : (
            <ExpiredEditModeReservation />
          )}
        </Flex>
        <Flex width="full" marginTop="20px" gap="20px">
          <DeliveryMapWithPinIcon
            boxSize="52px"
            color={isReservationExpired() ? 'accent0' : 'gray800'}
          />
          <Stack spacing="0px" align="flex-start">
            <Box>
              <Text
                color={isReservationExpired() ? 'accent0' : 'accent1'}
                variant="text4"
                lineHeight="short"
              >
                We&apos;ll deliver to:
              </Text>
              <Text marginTop="4px" variant="text4">
                My checkout address
              </Text>
              <Flex gap="4px" align="center" data-test-selector="delivery-address">
                {orderEditDeliverySlotChanged && (
                  <ExclamationCircleIcon
                    boxSize="16px"
                    color="promotionColorDark"
                    marginBottom="2px"
                  />
                )}
                {addressDisplay && <Text variant="text4" data-cs-mask="">{addressDisplay}</Text>}
              </Flex>
            </Box>
            <BaseButton
              variant="unstyled"
              display="inline-flex"
              paddingX="0px"
              height="auto"
              gap="2px"
              as={Link}
              to="/book-delivery"
            >
              <Text variant="text4" data-test-selector="change-delivery-address" color="accent5" className={editModeBasketActionText}>
                Edit delivery details
              </Text>
            </BaseButton>
          </Stack>
        </Flex>
        {isReservationExpired() && (
          <Box marginTop="24px">
            <Button
              variant="outlineDark"
              size="sm"
              width={['full', 'third']}
              disabled={loading}
              onClick={async () => {
                if (currentDeliverySlot.timeSlot?.id) {
                  await reserveSlot(currentDeliverySlot.timeSlot.id)
                }
              }}
            >
              Rebook
            </Button>
          </Box>
        )}
      </Box>
    </Stack>
  )
}

export const OrderEditModeAction = observer(OrderEditModeActionContainer)

const ExpiredEditModeReservation = observer(function ExpiredEditModeReservation() {
  const {currentDeliverySlot} = useBasketStore()
  const deliveryReservationDuration = useDeliveryReservationTime()

  const deliveryDate = currentDeliverySlot.timeSlot?.windowEndTime
    ? new Date(currentDeliverySlot.timeSlot.windowEndTime)
    : null

  return (
    <Flex gap="20px">
      <DeliveryDateTime hideDetails calendarBoxProps={{style: {width: 62}}} />
      <Stack spacing="0px">
        <Flex align="center" gap="4px">
          <ExclamationCircleIcon color="accent0" marginBottom="2px" />
          <Text color="accent0" variant="text4" data-test-selector="delivery-reservation-expired">
            Your delivery reservation has expired!
          </Text>
        </Flex>
        <Text variant="text4">{deliveryReservationDuration}</Text>
        {currentDeliverySlot.timeSlot && deliveryDate && (
          <>
            <DeliveryTimeAndDate
              startHour={currentDeliverySlot.timeSlot.startHour}
              endHour={currentDeliverySlot.timeSlot.endHour}
              textProps={{variant: 'text4'}}
              showDate={true}
            />
          </>
        )}
      </Stack>
    </Flex>
  )
})
